const { push: Push } = require('connected-react-router');
const { useCallback, useMemo } = require('react');
const { useDispatch } = require('react-redux');
const { context } = require('app-context');
const { useAppSelector } = require('utils/redux');
const SsoUtils = require('utils/sso');
const AuthConstants = require('services/auth-management/constants');
const { useCordovaOidcBrowser } = require('./useCordovaOidcBrowser');

const selectors = context.selectors.all;
const internals = {};

module.exports = {
    useSsoAuth
};
module.exports.internals = internals;

function useSsoAuth(props) {

    const {
        redirectUri,
        email,
        onCancel,
        onError,
        onRedirectRequestCreated,
        onCordovaCallbackCreated,
        redirectRequestFallback,
        setAuthCode,
        setAuthState,
        forceSSOLoginPrompt,
        forceSsoReauthentication,
        sso: requestedSso
    } = props || {};

    const currentSchool = useAppSelector(selectors.getCurrentSchool);
    const sso = requestedSso || currentSchool?.sso || '';
    const provider = SsoUtils.getSSOProvider(sso);
    const providerConfig = useMemo(() => SsoUtils.getSSOProviderConfig(sso), [sso]);
    const dispatch = useDispatch();

    const client = useMemo(() => {

        const issuerBase = providerConfig.getIssuerBase();
        const sessionManager = SsoUtils.createSessionManager({
            storageKeys: providerConfig.storageKeys
        });
        const e2eMockClient = internals.createE2EMockClient({
            email,
            onRedirectRequestCreated,
            provider,
            redirectUri,
            setAuthCode,
            setAuthState,
            sso
        });

        if (e2eMockClient) {
            return e2eMockClient;
        }

        return {
            async loginWithRedirect({ authorizationParams, openUrl }) {

                return SsoUtils.loginWithRedirect({
                    issuerBase,
                    clientId: providerConfig.clientId,
                    scope: providerConfig.scope,
                    authorizePath: providerConfig.authorizePath,
                    authorizationParams,
                    redirectRequestCreatedHandler: onRedirectRequestCreated,
                    openUrl,
                    sessionManager,
                    applyProviderLoginMethods: providerConfig.applyLoginMethods
                });
            },

            async handleRedirectCallback(callbackUrl) {

                return SsoUtils.handleRedirectCallback({
                    issuerBase,
                    clientId: providerConfig.clientId,
                    tokenPath: providerConfig.tokenPath,
                    redirectRequestFallback,
                    sessionManager,
                    callbackUrl
                });
            },

            async getIdTokenClaims() {

                return SsoUtils.getIdTokenClaims(sessionManager);
            },

            async clearSession() {

                return SsoUtils.clearSession(sessionManager);
            },

            async logout() {

                return SsoUtils.logout(sessionManager);
            }
        };
    }, [email, onRedirectRequestCreated, provider, providerConfig, redirectRequestFallback, redirectUri, setAuthCode, setAuthState, sso]);

    const redirectToLogin = useCallback(async () => {

        await client.logout();
        dispatch(Push(AuthConstants.authFlow.routes.home));
    }, [client, dispatch]);

    const cordovaBrowser = useCordovaOidcBrowser({
        redirectUri,
        onCallback: ({ code, state, callbackUrl }) => {

            if (onCordovaCallbackCreated) {
                onCordovaCallbackCreated({
                    callbackUrl
                });
            }

            setAuthCode?.(code);
            setAuthState?.(state);
        },
        onCancel: async () => {

            if (onCancel) {
                await onCancel();
                return;
            }

            await redirectToLogin();
        },
        onError: async (result) => {
            if (onError) {
                await onError(result);
                return;
            }

            await redirectToLogin();
        }
    });

    const logout = useCallback(async () => {

        return await client.logout();
    }, [client]);

    const clearSession = useCallback(async () => {

        await client.clearSession();
    }, [client]);

    const login = useCallback(async () => {

        const authorizationParams = internals.buildAuthorizationParams({
            email,
            forceSSOLoginPrompt,
            forceSsoReauthentication,
            provider,
            providerConfig,
            redirectUri,
            sso
        });

        SsoUtils.recordSsoRuntime('sso_login_start', {
            provider,
            sso,
            domain: providerConfig.domain,
            clientIdPresent: !!providerConfig.clientId,
            scope: providerConfig.scope,
            redirectUri,
            isCordova: !!window.cordova,
            cordovaPlatformId: window.cordova?.platformId || null,
            hasInAppBrowser: !!window.cordova?.InAppBrowser,
            loginMethods: authorizationParams.loginMethods || null,
            hasEmail: !!email
        });

        await client.loginWithRedirect({
            authorizationParams,
            openUrl: window.cordova ? cordovaBrowser.openUrl : undefined
        });
}, [client, cordovaBrowser.openUrl, email, forceSSOLoginPrompt, forceSsoReauthentication, provider, providerConfig, redirectUri, sso]);

    return {
        client,
        logout,
        clearSession,
        login,
        redirectToLogin,
        provider,
        domain: providerConfig.domain,
        clientId: providerConfig.clientId,
        scope: providerConfig.scope
    };
}

internals.buildAuthorizationParams = ({
    email,
    forceSSOLoginPrompt,
    forceSsoReauthentication,
    provider,
    providerConfig,
    redirectUri,
    sso
}) => {

    const loginMethods = providerConfig.formatLoginMethods(SsoUtils.getSSOLoginMethods(sso));

    const authorizationParams = {
        redirect_uri: redirectUri,
        scope: providerConfig.scope,
        userEmail: email
    };

    if (internals.shouldForcePromptLogin({
        forceSSOLoginPrompt,
        provider
    })) {
        authorizationParams.prompt = 'login';
    }

    if (provider === 'keycloak' && forceSsoReauthentication) {
        authorizationParams.prompt = 'login';
        authorizationParams.max_age = 0;
    }

    if (loginMethods) {
        authorizationParams.loginMethods = loginMethods;
    }

    return authorizationParams;
};

internals.shouldForcePromptLogin = ({ forceSSOLoginPrompt, provider }) => (
    provider === 'keycloak' && !!forceSSOLoginPrompt
);

internals.createE2EMockClient = (options) => {

    if (process.env.NODE_ENV === 'production') {
        return null;
    }

    // Keep the e2e-only provider mock out of the primary SSO implementation path.
    // Production builds compile this branch away because the env check is static.
    const E2ESsoProviderMock = require('./testing/e2eSsoProviderMock');

    if (!E2ESsoProviderMock.isEnabled()) {
        return null;
    }

    return E2ESsoProviderMock.createClient(options);
};
